/**
 * accountStepCopy.ts — every word of the checkout ACCOUNT STEP (the screen after
 * a guest pays): the two-step indicator per lane, the screen's opening line, the
 * "can't find the code?" help under the code box, and the sign-in view a
 * returning patient is switched to.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey-ux account step
 * (CTO feedback, 2026-09-24).
 *
 * THE CTO'S BRIEF, in his words: *"It should just be 'Enter the password' and
 * 'Re-enter the password.' Then match the passwords entered, and then enter the
 * code… ask them to check in the spam or junk folder… I don't think we need to
 * show the third step… Since this should only trigger for new patients, I don't
 * think we should give the sign-in option. If it is a patient who is already in
 * our database… detect that they already have an account and then prompt them
 * to sign in."*
 *
 * The register form's own words (the "Creating your account for …" line, the
 * password-mismatch sentence) live beside it in `auth/forms.tsx`, which must not
 * import from checkout.
 *
 * WHY A MODULE OF ITS OWN and not more lines in `celebrationCopy.ts`: that file
 * owns the celebration modal. The account step's words were only ever lodgers
 * there. Its three-step `ACCOUNT_STEPS` was deleted when the two branches were
 * integrated (nothing referenced it); `accountStageHelp(…, 'create')` ("…(or
 * sign in)…") is no longer rendered either, but `celebration.spec.tsx` still
 * pins it, so it stays until that describe block goes with it.
 */
import type { PaymentCaptureRoute } from './paymentCopy';

/**
 * Which way round the account step runs.
 *  - `form-first` — the in-flow guest: the journey still holds the email and
 *    date of birth, so the password comes first and "Create account" reveals the
 *    emailed-code gate.
 *  - `code-first` — a cold return (resume link): the code gate comes FIRST, and
 *    only then does the server's account-bootstrap tell us the email.
 */
export type AccountLane = 'form-first' | 'code-first';

/**
 * TWO STEPS, IN THE ORDER THE LANE REALLY RUNS THEM. The CTO dropped the third
 * ("Go to your account"): a correct code and Continue already take the patient
 * straight on to their confirmation, so a step for it described nothing they do.
 * The labels are the same two sentences on both lanes; only the ORDER differs,
 * because a wrong order is worse than no indicator at all.
 */
export const ACCOUNT_STEP_LABELS: Record<AccountLane, readonly [string, string]> = {
  'form-first': ['Choose a password', 'Enter the code we email you'],
  'code-first': ['Enter the code we email you', 'Choose a password'],
};

/**
 * The account step's opening line.
 *
 * ⚠️ THE OPENER IS A MONEY SENTENCE AND FOLLOWS THE CAPTURE ROUTE. "Payment is
 * done" is true only on the clinician-led (captured) route; on the hold route
 * nothing has been charged, so it says the order is placed. That rule is
 * `celebrationCopy.accountStageHelp`'s, restated here without its "(or sign
 * in)" — the sign-in choice left this screen — and `accountStep.spec.tsx` pins
 * the two openers against that function so they cannot drift apart.
 */
export function createAccountHelp(route: PaymentCaptureRoute): string {
  return route === 'immediate'
    ? 'Payment is done — create an account so we can save your plan and delivery details for next time.'
    : 'Your order is placed — create your account so we can save your plan and delivery details for next time.';
}

/**
 * Under the code box. The code goes to the address from the questionnaire, and
 * the commonest reasons a patient cannot see it are a junk filter and a short
 * delivery delay — both of which look, from the patient's side, exactly like
 * "the code never came". The Resend control sits directly below this line, and
 * a resend retires the earlier code (one live code per assessment), so the line
 * says "a new code" rather than implying the old one still works.
 */
export const CODE_DELIVERY_HELP =
  'Can’t find it? Check your spam or junk folder — it can take a minute to arrive. Still nothing? Press Resend code for a new one.';

/** The sign-in view's heading, once identity says this email already has an account. */
export const EXISTING_ACCOUNT_TITLE = 'Sign in to your account';

/**
 * Why the screen changed under them. Tied to the password field with
 * `aria-describedby`, and focus lands on that field, so a screen-reader user
 * hears the reason with the control rather than a view that silently swapped.
 */
export const EXISTING_ACCOUNT_REASON = 'You already have an account with this email — sign in to link this order to it.';

/** A way out for a returning patient who cannot remember their password. */
export const EXISTING_ACCOUNT_FORGOT = 'Forgotten your password? (opens in a new tab)';

/**
 * THE STEP THE NEW TAB CANNOT DO FOR THEM (review, 2026-09-24). The reset runs
 * in another tab, and that tab ends on its own "Sign in", which lands on the
 * patient dashboard — but the bind and the claim that put THIS paid order on the
 * account run only from this page's sign-in. A patient who signs in over there
 * gets a dashboard with no order on it and a paid order still owned by nobody.
 * So the line says the return trip out loud, and the link names it with
 * `aria-describedby` so it is read with the link, before anyone follows it.
 */
export const EXISTING_ACCOUNT_FORGOT_RETURN =
  'Reset it in the new tab, then come back to this page and sign in here — signing in on this page is what links this order to your account.';

/**
 * THE SIGN-IN BIND IS WAITING ON THE MAILBOX (identity#105, 2026-09-24).
 * identity refuses to link an assessment to an account whose email is not yet
 * confirmed (403 `email_not_verified`), so a patient who paid as a guest and
 * then signed in to an existing, UNCONFIRMED account lands on "Confirm your
 * email" with their order not yet on the account. That is a hold, not a
 * failure. So this is said calmly (an `info` status, never the red alert) and
 * takes the place of that screen's usual help line rather than repeating it.
 * It says nothing about money — the order is placed on every route, and the
 * capture route's "Payment is done" is not this line's to make.
 *
 * ⚠️ IT SAYS "COME BACK TO THIS PAGE" BECAUSE ONLY THIS PAGE CAN ADD THE ORDER
 * (review, 2026-09-25). The owed bind lives in THIS tab's memory (`bindOwed`,
 * `bindHeldForEmail` in CheckoutFlow). The verify link does no bind and no
 * claim — /auth shows "Email verified" and a dashboard link — and after a
 * reload or a new tab the signed-in resume asks orders by account, finds the
 * still-guest-owned order under nobody, and shows the plan step. Nothing binds
 * again from there. The earlier "…and we'll add this order to your account"
 * promised something the link cannot do on its own. Same call as
 * `EXISTING_ACCOUNT_FORGOT_RETURN` for the password reset: say the return trip
 * out loud. (Carrying the owed bind across a reload is the durable fix; see
 * CLAUDE.md.)
 */
export const BIND_WAITS_FOR_EMAIL =
  'Confirm your email with the link we sent you, then come back to this page and we’ll add this order to your account. Please keep this page open until then — this is where the order is added.';

/**
 * THE LINK THE HOLD POINTS AT MAY NOT EXIST ANY MORE (review, 2026-09-25).
 * Signing in sends no verification email (identity's login issues a session and
 * nothing else), and a link lives 24 hours (`VERIFY_TTL_MS`), so a patient who
 * registered days ago has only a dead link, or none they can find. Shown ONLY on
 * the "Confirm your email" screen, whose link card below carries the Resend link
 * button this names — never off it, where there is no such button.
 */
export const BIND_WAITS_RESEND_HINT = 'Can’t find the email, or has the link expired? Press Resend link below for a new one.';

/**
 * The quiet exit from a revealed form-first code gate. Pressing "Create account"
 * swaps the form for the code box; without this, a patient who realises the
 * password they typed twice is not the one they meant (caps lock, a password
 * manager value they never saved) could only go forward — and going forward
 * creates the account WITH that password.
 */
export const BACK_TO_PASSWORD = 'Back to change your password';

/**
 * Where the keyboard lands once the form-first code is accepted. The code box
 * and its Continue button leave the page at that moment, so focus needs a place
 * to go that stays true whatever register answers next (created → the claim's
 * loader; an existing account → the sign-in view, which takes focus itself; a
 * failure → the form's own announced error, right below this line).
 */
export const EMAIL_CONFIRMED_STATUS = 'Your email is confirmed.';
